import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { doc, getDocFromServer, setDoc, serverTimestamp } from "firebase/firestore";
import { MultiFactorResolver, getMultiFactorResolver, RecaptchaVerifier } from "firebase/auth";
import { auth } from "@/lib/firebase";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
  resetPassword,
} from "@/firebase/auth.custom";
import { useAuth } from "@/firebase/useAuth";
import { db } from "@/lib/firebase";
import ZillHubLogoBlue from "@/assets/ZillHubLogoBlue_noBG.png";
import { TwoFactorVerify } from "@/components/TwoFactorVerify";

const passwordHint = "Must be at least 8 characters with an uppercase letter, a number, and a special character (!@#$%^&*).";

type AuthMode = "login" | "signup";

type LocationState = {
  from?: { pathname: string } | null;
};

export default function Login() {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"form" | "google" | "reset" | null>(null);
  const [previousUser, setPreviousUser] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [recaptchaSolved, setRecaptchaSolved] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const redirectTo = useMemo(() => {
    const state = location.state as LocationState | null;
    return state?.from?.pathname ?? "/dashboard";
  }, [location.state]);

  useEffect(() => {
    if (loading || !user) {
      // Reset previousUser on logout so the next sign-in is treated as new auth
      if (!loading && !user && previousUser !== null) setPreviousUser(null);
      return;
    }
    
    // Check if this is a new authentication (user changed from null to a user)
    const isNewAuth = previousUser === null;
    
    setPreviousUser(user.uid);
    
    // Only redirect if user just authenticated
    if (!isNewAuth) {
      return;
    }

    // Check if user has completed onboarding
    const checkOnboarding = async () => {
      try {
        const userDocRef = doc(db(), "users", user.uid);
        const snap = await getDocFromServer(userDocRef);
        const userData = snap.data();
        
        // Bootstrap a minimal user profile for new sign-ins
        if (!snap.exists()) {
          await setDoc(userDocRef, {
            uid: user.uid,
            email: user.email ?? null,
            displayName: user.displayName ?? (user.email ? user.email.split("@")[0] : null),
            createdAt: serverTimestamp(),
            // no onboarding flags yet
          }, { merge: true });
        }
        
        // Check both new and legacy onboarding completion flags
        const completed =
          !!userData?.onboardingCompletedAt ||
          userData?.hasOnboarded === true; // MVP legacy flag
        
        // Validate redirect URL - only allow internal paths
        const safeRedirectTo = redirectTo.startsWith('/') ? redirectTo : '/dashboard';
        
        // If user hasn't completed onboarding, redirect there
        if (!completed) {
          navigate("/onboarding", { replace: true });
          return;
        }
        
        // Otherwise redirect to intended destination
        navigate(safeRedirectTo, { replace: true });
      } catch (err) {
        // Error checking onboarding, default to dashboard
        navigate('/dashboard', { replace: true });
      }
    };

    checkOnboarding();
  }, [user, loading, navigate, redirectTo, previousUser]);

  // Initialize reCAPTCHA when MFA is required
  useEffect(() => {
    if (mfaResolver && !window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(auth(), 'recaptcha-container', {
          size: 'normal',
          callback: () => {
            setRecaptchaSolved(true);
          },
          'expired-callback': () => {
            setRecaptchaSolved(false);
          }
        });
        window.recaptchaVerifier.render().then((widgetId: number) => {
          window.recaptchaWidgetId = widgetId;
        });
      } catch (error) {
        // Error initializing reCAPTCHA
      }
    }

    // Cleanup function
    return () => {
      if (window.recaptchaVerifier && !mfaResolver) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = undefined;
        window.recaptchaWidgetId = undefined;
      }
    };
  }, [mfaResolver]);

  const isSubmitting = pendingAction === "form" || pendingAction === "google";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    // Check server-side rate limit first (for email/password only)
    if (mode === "login" && email) {
      // For rate limiting, use the actual input (email or username)
      const rateLimitIdentifier = email.trim();
      
      try {
        const rateLimitRes = await fetch("/api/auth/check-rate-limit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: rateLimitIdentifier }),
        });
        
        const rateLimitData = await rateLimitRes.json();
        
        if (!rateLimitData.allowed) {
          setError(rateLimitData.error || "Too many failed attempts. Please try again later.");
          setLockoutUntil(rateLimitData.lockedUntil || Date.now() + 5 * 60 * 1000);
          return;
        }
      } catch (err) {
        // On rate limit check failure, proceed anyway (fail open)
        if (import.meta.env.DEV) {
          console.error("Rate limit check failed:", err);
        }
      }
    }

    // Check if user is locked out (client-side backup)
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const minutesLeft = Math.ceil((lockoutUntil - Date.now()) / 60000);
      setError(`Too many failed attempts. Please try again in ${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''}.`);
      return;
    }
    
    // Clear lockout if time has passed
    if (lockoutUntil && Date.now() >= lockoutUntil) {
      setLockoutUntil(null);
      setFailedAttempts(0);
    }

    setError(null);
    setInfoMessage(null);
    setPendingAction("form");

    try {
      if (mode === "login") {
        // Check if input is username (no @) or email (has @)
        let authEmail = email.trim();
        
        if (!authEmail.includes('@')) {
          // It's a username - translate it to authEmail
          try {
            const response = await fetch('/api/auth/username-to-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: authEmail.toLowerCase() })
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.error || 'Username not found');
            }
            
            const data = await response.json();
            authEmail = data.authEmail;
          } catch (translationError: any) {
            setError(translationError.message === 'Username not found' 
              ? 'Username not found. Please check your username or use your email address.'
              : 'Unable to verify username. Please try again or use your email address.');
            setPendingAction(null);
            return;
          }
        }
        
        // Now authenticate with authEmail (either original email or translated)
        await loginWithEmail({ email: authEmail, password });
        // Reset failed attempts on successful login (both client and server)
        setFailedAttempts(0);
        setLockoutUntil(null);
        
        // Reset server-side rate limit
        try {
          await fetch("/api/auth/reset-rate-limit", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: authEmail }),
          });
        } catch (err) {
          // Non-critical, ignore
        }
      } else {
        const trimmedDisplayName = displayName.trim();
        if (!trimmedDisplayName) {
          setError("Please enter a display name.");
          setPendingAction(null);
          return;
        }
        if (trimmedDisplayName.length > 30) {
          setError("Display name must be 30 characters or less.");
          setPendingAction(null);
          return;
        }
        // Basic character validation - allow letters, numbers, spaces, hyphens, underscores, apostrophes
        const validNamePattern = /^[a-zA-Z0-9\s\-_']+$/;
        if (!validNamePattern.test(trimmedDisplayName)) {
          setError("Display name can only contain letters, numbers, spaces, hyphens, underscores, and apostrophes.");
          setPendingAction(null);
          return;
        }
        
        // Validate password meets Firebase requirements
        if (password.length < 8) {
          setError("Password must be at least 8 characters long.");
          setPendingAction(null);
          return;
        }
        if (!/[A-Z]/.test(password)) {
          setError("Password must contain at least one uppercase letter.");
          setPendingAction(null);
          return;
        }
        if (!/[0-9]/.test(password)) {
          setError("Password must contain at least one number.");
          setPendingAction(null);
          return;
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
          setError("Password must contain at least one special character (!@#$%^&* etc.).");
          setPendingAction(null);
          return;
        }
        
        // Server will validate display name for inappropriate content
        await registerWithEmail({ email, password, displayName: trimmedDisplayName });
      }
      // Auth state will update via useAuth hook, triggering the useEffect
    } catch (err: any) {
      // Check if MFA is required
      if (err?.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(err.resolver);
        setPendingAction(null);
        return;
      }
      
      let message = "Unable to authenticate. Please try again.";
      
      if (err instanceof Error) {
        // Convert Firebase error codes to friendly messages
        if (err.message.includes("auth/invalid-credential") || err.message.includes("auth/wrong-password") || err.message.includes("auth/user-not-found")) {
          // Generic message to prevent email enumeration
          message = "Invalid email or password. Please try again.";
          setFailedAttempts(prev => prev + 1);
          
          // Record failed attempt on server (for email/password login)
          if (mode === "login" && email) {
            try {
              await fetch("/api/auth/record-failed-login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
              });
            } catch (err) {
              // Non-critical, ignore
            }
          }
          
          // Lock out after 5 failed attempts for 5 minutes (client-side backup)
          if (failedAttempts + 1 >= 5) {
            const lockoutTime = Date.now() + 5 * 60 * 1000; // 5 minutes
            setLockoutUntil(lockoutTime);
            message = "Too many failed attempts. Please try again in 5 minutes.";
          }
        } else if (err.message.includes("auth/email-already-in-use")) {
          // For signup, this is acceptable to reveal
          message = "An account with this email already exists. Please sign in.";
        } else if (err.message.includes("auth/weak-password")) {
          // Show specific password requirements
          message = "Password must be at least 8 characters with an uppercase letter, a number, and a special character.";
        } else if (err.message.includes("PASSWORD_DOES_NOT_MEET_REQUIREMENTS")) {
          // Parse Firebase password requirements error
          if (err.message.includes("upper case")) {
            message = "Password must contain at least one uppercase letter.";
          } else if (err.message.includes("non-alphanumeric")) {
            message = "Password must contain at least one special character (!@#$%^&* etc.).";
          } else if (err.message.includes("lower case")) {
            message = "Password must contain at least one lowercase letter.";
          } else {
            message = "Password must be at least 8 characters with an uppercase letter, a number, and a special character.";
          }
        } else if (err.message.includes("auth/invalid-email")) {
          // Generic message
          message = "Invalid credentials. Please try again.";
        } else if (err.message.includes("auth/too-many-requests")) {
          message = "Too many failed attempts. Please try again later.";
        } else if (!err.message.includes("Firebase:")) {
          // If it's a custom error message (not from Firebase), use it directly
          message = err.message;
        }
      }
      
      setError(message);
      setPendingAction(null);
    }
  };

  const handleGoogleLogin = async () => {
    if (isSubmitting) return;
    setError(null);
    setInfoMessage(null);
    setPendingAction("google");

    try {
      await loginWithGoogle();
      // Auth state will update via useAuth hook, triggering the useEffect
    } catch (err: any) {
      // Check if MFA is required
      if (err?.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth(), err);
          setMfaResolver(resolver);
          setPendingAction(null);
          return;
        } catch (resolverErr) {
          // Error getting MFA resolver
          setError("Multi-factor authentication is required but could not be initialized. Please try signing in with email/password instead.");
          setPendingAction(null);
          return;
        }
      }
      const message = err instanceof Error ? err.message : "Unable to sign in with Google.";
      setError(message);
      setPendingAction(null);
    }
  };

  const handleResetPassword = async () => {
    if (pendingAction === "reset" || !email) {
      if (!email) {
        setError("Enter your email address or username to receive a reset link.");
      }
      return;
    }

    setError(null);
    setInfoMessage(null);
    setPendingAction("reset");
    
    try {
      // Check if input is username (no @) or email (has @)
      let resetEmail = email.trim();
      
      if (!resetEmail.includes('@')) {
        // It's a username - translate it to authEmail
        try {
          const response = await fetch('/api/auth/username-to-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: resetEmail.toLowerCase() })
          });
          
          if (!response.ok) {
            throw new Error('Username not found');
          }
          
          const data = await response.json();
          resetEmail = data.authEmail;
        } catch (translationError) {
          setError('Username not found. Please check your username or use your email address.');
          setPendingAction(null);
          return;
        }
      }
      
      await resetPassword(resetEmail);
      setInfoMessage("If an account exists with this email or username, you'll receive password reset instructions shortly.");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to send reset email.";
      setError(message);
    } finally {
      setPendingAction(null);
    }
  };

  // If MFA is required, show the 2FA verification screen
  if (mfaResolver) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-soft px-4 py-10">
        <div className="w-full max-w-md mx-auto space-y-4">
          {/* reCAPTCHA container for SMS verification */}
          <div id="recaptcha-container" className="flex justify-center"></div>
          
          <TwoFactorVerify
            resolver={mfaResolver}
            recaptchaSolved={recaptchaSolved}
            onSuccess={() => {
              // Clear reCAPTCHA
              if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = undefined;
                window.recaptchaWidgetId = undefined;
              }
              setRecaptchaSolved(false);
              setMfaResolver(null);
              setPreviousUser(null); // Reset to trigger redirect
              // Auth state will update and redirect will happen automatically
            }}
            onCancel={() => {
              // Clear reCAPTCHA
              if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = undefined;
                window.recaptchaWidgetId = undefined;
              }
              setRecaptchaSolved(false);
              setMfaResolver(null);
              setPreviousUser(null); // Reset state
              setError("Sign-in cancelled. Please try again.");
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-soft px-4 py-10">
      <div className="w-full max-w-md mx-auto space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-hope hover:text-dream transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to ZillHub
        </Link>
        
        <div className="space-y-6 rounded-[20px] bg-cardWhite p-8 shadow-sm border border-gray-100">
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <img src={ZillHubLogoBlue} alt="ZillHub" className="h-16 w-auto object-contain" />
            </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
            {mode === "login" ? "Welcome" : "Create your account"}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {mode === "login"
              ? "Sign in or create an account to access your resilience journey."
              : "Join ZillHub to begin your resilience journey."}
          </p>
        </div>

        {error && (
          <div className="rounded-[14px] border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {infoMessage && (
          <div className="rounded-[14px] border border-resilience bg-tealMist p-3 text-sm text-gray-900">
            {infoMessage}
          </div>
        )}

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2 text-left">
            <Label htmlFor="email">{mode === "login" ? "Email or Username" : "Email"}</Label>
            <Input
              id="email"
              type={mode === "login" ? "text" : "email"}
              autoComplete={mode === "login" ? "username" : "email"}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={mode === "login" ? "you@example.com or your_username" : "you@example.com"}
              className="bg-cardWhite text-gray-900 placeholder:text-gray-600 border-gray-300 focus:border-hope focus:ring-hope"
            />

          </div>

          <div className="space-y-2 text-left">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              className="bg-cardWhite text-gray-900 placeholder:text-gray-600 border-gray-300 focus:border-hope focus:ring-hope"
            />
            {mode === "signup" && (
              <p className="text-xs text-gray-600">{passwordHint}</p>
            )}
          </div>

          {mode === "signup" && (
            <div className="space-y-2 text-left">
              <Label htmlFor="displayName">Display name</Label>
              <Input
                id="displayName"
                type="text"
                autoComplete="name"
                required
                minLength={1}
                maxLength={30}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Your public name"
                className="bg-cardWhite text-gray-900 placeholder:text-gray-600 border-gray-300 focus:border-hope focus:ring-hope"
              />
              <p className="text-xs text-gray-600">This name will be visible to friends and the community.</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-6 py-3 font-semibold text-white border-0 shadow-lg transition-all"
            disabled={isSubmitting}
          >
            {mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-xl border-hope text-hope hover:bg-sky flex items-center justify-center gap-3"
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20454Z" fill="#4285F4"/>
              <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
              <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
              <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
            </svg>
            {mode === "login" ? "Sign in with Google" : "Sign up with Google"}
          </Button>

          {mode === "login" && (
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={pendingAction === "reset"}
              className="text-sm font-semibold text-hope hover:text-dream transition-colors"
            >
              Forgot your password?
            </button>
          )}
        </div>

        {mode === "login" ? (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-center">
            <p className="text-white text-lg font-semibold mb-4">New to ZillHub?</p>
            <div className="space-y-3">
              <Button
                type="button"
                onClick={() => {
                  setMode("signup");
                  setError(null);
                  setInfoMessage(null);
                }}
                className="w-full rounded-xl bg-white text-purple-600 font-bold hover:bg-gray-50 border-0 shadow-lg"
              >
                Create a New Account
              </Button>
              <Button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isSubmitting}
                className="w-full rounded-xl bg-white text-purple-600 font-bold hover:bg-gray-50 border-0 shadow-lg flex items-center justify-center gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20454Z" fill="#4285F4"/>
                  <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                  <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                  <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
                </svg>
                Create a New Account with Google
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">Already have an account?</p>
            <Button
              type="button"
              onClick={() => {
                setMode("login");
                setError(null);
                setInfoMessage(null);
              }}
              variant="outline"
              className="w-full rounded-xl border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold"
            >
              Back to Sign In
            </Button>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
